import { Box, Checkbox, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from '@material-ui/core';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { LinkedSegments } from '../../../../components/Curation/Sections';
import { ImageUpload } from '../../../../components/ImageUpload';
import { SubmitControl } from '../../../../components/SubmitControl';
import api from '../../../../services/api/base';
import { FeaturedCardType, IFeaturedCard, IFeaturedCardObject, StoryType } from '../../../../services/types';
import { getPathnameFromURI } from '../../../../services/utils';
import FeaturedCard from './FeaturedCard';
import useStyles from './styles';
import { uploadFile } from '../../../../services/api/S3';
import { createFeaturedCard, updateFeaturedCard } from '../../../../services/api/FeaturedCard';
import FormTextField from '../../../../components/FormTextField';
import { convertFirstCharacterToUppercase } from '../../../../services/utils';

interface IProps {
  mode: 'create' | 'edit';
  featuredCard?: Partial<IFeaturedCard>;
  object?: IFeaturedCardObject;
  onPressCancel: () => void;
  onPressSave: (f: IFeaturedCard, createAnother: boolean) => void;
  createAnother?: boolean;
}

const typeToColor = (type: FeaturedCardType) => {
  switch (type) {
    case 'story':
      return '#ff008a';
    case 'link':
      return '#4a90e2';
    case 'collection':
      return '#9013fe';
  }
};

const Detail = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { mode, featuredCard } = props;

  const DefaultImage = `featured/collection_default_banner.png`;

  const [isEditing, setIsEditing] = useState<boolean>(mode === 'create');
  const [pendingUpload, setPendingUpload] = useState<boolean>();

  const [subTitle, setSubTitle] = useState<string>(featuredCard?.subTitle || '');
  const [actionText, setActionText] = useState<string>(featuredCard?.actionText || '');
  const [label, setLabel] = useState<string>(featuredCard?.label || '');
  const [isEvent, setIsEvent] = useState<boolean>(featuredCard?.isEvent || false);
  const [createAnother, setCreateAnother] = useState<boolean>(props.createAnother || false);

  const [coverUrl, setCoverUrl] = useState<string>(featuredCard?.coverUrl || '');
  const [fileName, setFileName] = useState<string>('');

  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  const onFileLoaded = async (file: File, blob: Blob, imageContent: string) => {
    setCoverUrl(imageContent);
    setFileName(file.name);

    try {
      setPendingUpload(true);
      const result = await uploadFile('collection_image.jpg', blob);

      if (result?.data && result?.status === 200) {
        setCoverUrl(result.data.url);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPendingUpload(false);
    }
  };

  const reset = () => {
    setSubTitle('');
    setActionText('');
    setLabel('');
    setCoverUrl('');
    setFileName('');
  };

  const onKeyDownImage = (e: React.KeyboardEvent) => {
    e.preventDefault();

    if (e.key === 'Backspace') {
      setFileName('');
      setCoverUrl('');
    }
  };

  const onClickSave = async () => {
    const f: IFeaturedCard = {
      title: featuredCard?.title,
      coverUrl: getPathnameFromURI(coverUrl) || '',
      type: featuredCard?.type || FeaturedCardType.Story,
      object: props.object,
      targetId: featuredCard?.targetId,
      label,
      isEvent,
      subTitle,
      actionText,
    };

    try {
      const result = await (mode === 'create' ? createFeaturedCard(f) : updateFeaturedCard(featuredCard?.id || 0, f));

      if (api.isResponseSuccess(result)) {
      }
    } catch (e) {
      console.log(e);
    } finally {
      props.onPressSave(f, createAnother);
    }
  };

  useEffect(() => {
    setSubTitle(featuredCard?.subTitle || '');
    setActionText(featuredCard?.actionText || '');
    setLabel(featuredCard?.label || '');
    setCoverUrl(featuredCard?.coverUrl || '');
    setIsEvent(featuredCard?.isEvent || false);
  }, [props]);

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!subTitle) {
      messageSet.add('A sub title cannot be empty.');
    }

    if (!actionText) {
      messageSet.add('An action text cannot be empty.');
    }

    const messages = Array.from(messageSet);

    setValidationMessages(messages);
  }, [subTitle, actionText]);

  return (
    <Box className={classes.detailWrap}>
      <Box className={classes.bodyScroll}>
        <Grid container direction="row">
          <Grid item xs={6} style={{ flexBasis: 331 }}>
            <Grid container direction="column" style={{ paddingRight: 30 }}>
              {mode === 'edit' && !isEditing && (
                <Grid item className={classes.detailItem}>
                  <Typography className={classes.detailTitle}>ID</Typography>
                  <Typography className={classes.detailValue}>{featuredCard?.id}</Typography>
                </Grid>
              )}
              <Grid item style={{ display: 'flex' }}>
                <Grid item className={classes.detailItem} style={{ marginRight: 40 }}>
                  <Typography className={classes.detailTitle}>Type</Typography>
                  <Typography className={`${classes.detailValue} ${classes.capital}`}>{featuredCard?.type}</Typography>
                </Grid>
                {featuredCard?.type !== FeaturedCardType.Link && (
                  <Grid item className={classes.detailItem} style={{ marginRight: 40 }}>
                    <Typography className={classes.detailTitle}>Countries</Typography>
                    <Typography className={`${classes.detailValue} ${classes.capital}`}>{featuredCard?.availableCountryCountText}</Typography>
                  </Grid>
                )}
                <Grid item className={classes.detailItem}>
                  <Typography className={classes.detailTitle}>Content Type</Typography>
                  <Typography className={`${classes.detailValue} ${classes.capital}`}>
                    {(featuredCard?.type === FeaturedCardType.Story && featuredCard?.object?.story.type) ||
                      (featuredCard?.type === FeaturedCardType.Season && featuredCard?.object?.story.type) ||
                      (featuredCard?.type === FeaturedCardType.Collection &&
                        featuredCard?.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ')) ||
                      '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item className={classes.detailItem}>
                <Typography className={classes.detailTitle}>
                  <span className={classes.capital}>{featuredCard?.type === FeaturedCardType.Collection ? 'Tracking' : featuredCard?.type}</span> ID
                </Typography>
                <Typography className={classes.detailValue}>{featuredCard?.targetId}</Typography>
              </Grid>

              {isEditing && (
                <React.Fragment>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Title</Typography>
                    <Typography className={classes.detailValue}>{featuredCard?.title}</Typography>
                  </Grid>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Sub Title</Typography>
                    <FormTextField
                      fullWidth
                      onValueChange={setSubTitle}
                      defaultValue={featuredCard?.subTitle || ''}
                      maxLength={50}
                      showTextCount={true}
                    />
                  </Grid>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Action Text</Typography>
                    <TextField fullWidth onChange={e => setActionText(e.target.value)} value={actionText} style={{ fontSize: 14 }} />
                  </Grid>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Label</Typography>
                    <TextField fullWidth onChange={e => setLabel(e.target.value)} value={label} />
                  </Grid>
                </React.Fragment>
              )}

              <Grid item className={classes.detailItem}>
                <Typography className={classes.detailTitle}>Is Event</Typography>
                <RadioGroup
                  row
                  value={isEvent ? '1' : '0'}
                  name="isEvent"
                  onChange={e => {
                    setIsEvent(e.target.value === '1' ? true : false);
                  }}
                >
                  <FormControlLabel disabled={!isEditing} control={<Radio value="0" color="primary" />} label="No" />
                  <FormControlLabel disabled={!isEditing} control={<Radio value="1" color="primary" />} label="Yes" />
                </RadioGroup>
              </Grid>
              {mode === 'edit' && !isEditing && (
                <React.Fragment>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Created At</Typography>
                    <Typography className={classes.detailValue}>{moment(featuredCard?.createdAt).format('llll')}</Typography>
                  </Grid>
                  <Grid item className={classes.detailItem}>
                    <Typography className={classes.detailTitle}>Card Setting</Typography>
                    <FeaturedCard
                      type={featuredCard?.type || FeaturedCardType.Story}
                      object={featuredCard?.object}
                      targetId={featuredCard?.targetId}
                      title={featuredCard?.title || ''}
                      coverUrl={featuredCard?.displayCoverUrl || featuredCard?.coverUrl}
                      isEditing={false}
                    />
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          </Grid>
          <Grid item xs={6}>
            {isEditing && (
              <Grid container direction="column">
                <Typography className={classes.detailTitle}>Cover Replacement (Optional)</Typography>
                <ImageUpload onFileLoaded={onFileLoaded} src={coverUrl} isPending={pendingUpload} />
                {(featuredCard?.type === 'story' || featuredCard?.type === 'season') && (
                  <Typography>*{featuredCard?.type.replace(/^s/, 'S')} cover will be displayed as a default.</Typography>
                )}
              </Grid>
            )}
            {!isEditing && featuredCard && (
              <Grid container>
                <Grid item xs={6}>
                  <Typography className={classes.detailTitle}>Linked</Typography>
                </Grid>
                <Grid item xs={6} style={{ textAlign: 'right' }}>
                  Results: {featuredCard.linkedCurations?.length || 0}
                </Grid>
                <LinkedSegments linkedCurations={featuredCard.linkedCurations} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box display="flex" alignItems="center" alignContent="flex-end" paddingRight="20px">
        <SubmitControl
          mode={isEditing ? (featuredCard?.id ? 'edit' : 'create') : 'view'}
          submitTitle="SAVE"
          validationMessages={validationMessages}
          disableModal={true}
          onCancelClick={() => {
            props.onPressCancel();
            reset();
          }}
          onEditClick={() => {
            setIsEditing(true);
          }}
          onSubmitClick={onClickSave}
          pending={pendingUpload}
          prepend={
            mode === 'create' ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createAnother}
                    onChange={e => {
                      setCreateAnother(e.target.checked);
                    }}
                    name="createAnother"
                    color="primary"
                  />
                }
                label="Create another"
              />
            ) : (
              undefined
            )
          }
        />
      </Box>
    </Box>
  );
};

export default Detail;
